import React, { useState, useEffect, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Chip,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
  Snackbar
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useThemeMode } from "../contexts/ThemeContext";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SecurityIcon from "@mui/icons-material/Security";
import DownloadIcon from "@mui/icons-material/Download";
import apiService from "../services/api";
import Lottie from "lottie-react";
import aiAnimation from "../assets/ai-search.json";


interface UploadZoneProps {
  onUploadSuccess?: (jobId: string) => void;
  onUploadError?: (error: string) => void;
  onExtractionComplete?: (jobId: string, transactions: any[]) => void;
}

interface SubscriptionCheck {
  isActive: boolean;
  plan: number | string | null;
  period: number | null;
  monthlyLimit: number;
  extraPages?: number;
  totalAvailable?: number;
  pagesUsed: number;
  pagesReserved?: number;
  pagesRemaining: number;
  status: number | null;
  periodKey?: string | null;
}

// Extra pages pricing - MUST be more expensive than plans
const EXTRA_PAGES_OPTIONS = [
  { pages: 100, price: 35 },   // More than Starter's $35
  { pages: 500, price: 125 },  // More than Business's $125
  { pages: 1000, price: 200 }  // More than Enterprise's $200
];

const getPlanName = (plan: number | string | null) => {
  if (plan === "Free" || plan === null) return "Free";
  const planValue = typeof plan === 'string' ? parseInt(plan, 10) : plan;
  switch (planValue) {
    case 501900000: return "Starter";
    case 501900001: return "Business";
    case 501900002: return "Enterprise";
    default: return "Free Trial";
  }
};

const getStatusLabel = (status: number | string | null) => {
  if (status === "Active" || status === 0) return "Active";
  if (status === "Inactive" || status === 1) return "Inactive";
  if (status === "Suspended" || status === 2) return "Suspended";
  return "Unknown";
};

const getStatusColor = (status: number | string | null): "success" | "warning" | "error" | "default" => {
  if (status === "Active" || status === 0) return "success";
  if (status === "Inactive" || status === 1) return "error";
  if (status === "Suspended" || status === 2) return "warning";
  return "default";
};

const InfoStat: React.FC<{
  label: string;
  value: string | number;
  helper?: string;
  icon?: React.ReactNode;
}> = ({ label, value, helper, icon }) => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: mode === 'light' 
          ? alpha(theme.palette.primary.main, 0.02)
          : alpha(theme.palette.primary.main, 0.05),
        border: `1px solid ${mode === 'light' 
          ? alpha(theme.palette.primary.main, 0.1)
          : alpha(theme.palette.primary.main, 0.2)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        {icon && <Box sx={{ color: theme.palette.secondary.main }}>{icon}</Box>}
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </Typography>
      {helper && (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      )}
    </Box>
  );
};

const UploadZone: React.FC<UploadZoneProps> = ({
  onUploadSuccess,
  onUploadError,
  onExtractionComplete
}) => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const navigate = useNavigate();

  const [uploadState, setUploadState] = useState<"idle" | "uploading" | "extracting" | "complete" | "error">("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [extractionStep, setExtractionStep] = useState(0);
  const [transactionCount, setTransactionCount] = useState(0);
  const [subscription, setSubscription] = useState<SubscriptionCheck | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const steps = ["Uploading PDF", "Extracting Transactions", "Complete"];
  const extractionSteps = ["Reading PDF structure", "Detecting transactions", "Applying AI categorisation"];

  const loadSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      const res = await apiService.getSubscription();
      setSubscription(res as SubscriptionCheck | null);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || "Unable to verify subscription.";
      setSubscription(null);
      setSubscriptionError(msg);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  useEffect(() => {
    loadSubscription();
  }, []);

  useEffect(() => {
    if (uploadState === "complete" && currentJobId) {
      if (window.location.pathname === "/") {
        navigate(`/transactions/${currentJobId}`, { replace: true });
        const timer = setTimeout(() => {
          if (window.location.pathname === "/") {
            window.location.href = `/transactions/${currentJobId}`;
          }
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [uploadState, currentJobId, navigate]);

  const usage = useMemo(() => {
    const baseQuota = Number(subscription?.monthlyLimit ?? 0);
    const extraPages = Number(subscription?.extraPages ?? 0);
    const totalAvailable = Number(subscription?.totalAvailable ?? baseQuota + extraPages);
    const used = Number(subscription?.pagesUsed ?? 0);
    const reserved = Number(subscription?.pagesReserved ?? 0);
    const remaining = Number(subscription?.pagesRemaining ?? 0);
    const percent = totalAvailable > 0 ? Math.min((used / totalAvailable) * 100, 100) : 0;
    return { baseQuota, extraPages, totalAvailable, used, reserved, remaining, percent };
  }, [subscription]);

  const usageColor = useMemo(() => {
    if (usage.percent >= 90) return theme.palette.error.main;
    if (usage.percent >= 70) return theme.palette.warning.main;
    return theme.palette.success.main;
  }, [usage.percent, theme]);

  // Check if user has an active subscription
  const hasActiveSubscription = useMemo(() => {
  // Use the isActive flag from the API response
  return !!subscription && subscription.isActive === true;
}, [subscription]);

  const canUpload = useMemo(() => {
    return hasActiveSubscription && usage.remaining > 0;
  }, [hasActiveSubscription, usage.remaining]);

  const quotaMessage = useMemo(() => {
    if (subscriptionLoading) return null;
    if (subscriptionError) return subscriptionError;
    if (!subscription) return "No subscription found.";
    if (!hasActiveSubscription) return "No active subscription. Please select a plan to start.";
    if (usage.remaining <= 0) return "No remaining pages available. Please buy extra pages or upgrade your plan.";
    return null;
  }, [subscription, subscriptionLoading, subscriptionError, hasActiveSubscription, usage.remaining]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "application/pdf": [".pdf"] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    disabled: uploadState !== "idle" || !canUpload,
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (!canUpload) {
        const msg = quotaMessage || "Upload is not available at the moment.";
        setUploadError(msg);
        onUploadError?.(msg);
        return;
      }
      if (rejectedFiles.length > 0) {
        const msg = "File must be a PDF and less than 10MB.";
        setUploadError(msg);
        onUploadError?.(msg);
        return;
      }
      if (acceptedFiles.length > 0) {
        await handleFileUpload(acceptedFiles[0]);
      }
    }
  });

  const checkQuotaBeforeUpload = async (): Promise<{ allowed: boolean; message?: string }> => {
  try {
    const sub = await apiService.getSubscription();
    // Use isActive instead of status
    if (!sub || !sub.isActive) {
      return { allowed: false, message: "No active subscription or page allowance found." };
    }
    if (Number(sub.pagesRemaining ?? 0) <= 0) {
      return { allowed: false, message: "No remaining pages available. Please buy extra pages or upgrade your plan." };
    }
    setSubscription(sub as SubscriptionCheck | null);
    return { allowed: true };
  } catch (e: any) {
    return { allowed: false, message: e?.response?.data?.error || e?.message || "Unable to verify subscription before upload." };
  }
};

  const handleBuyExtraPages = async (pages: number) => {
    // Check if user has active subscription first
    
    try {
      const origin = window.location.origin;
      const result = await apiService.createCheckoutSession({
        checkoutType: "extra_pages",
        extraPages: pages as 100 | 500 | 1000,
        successUrl: `${origin}/subscription?purchase=success`,
        cancelUrl: `${origin}/subscription?purchase=cancelled`
      });
      window.location.href = result.url;
    } catch (error) {
      console.error("Failed to create extra pages checkout:", error);
      setUploadError("Failed to start checkout. Please try again.");
    }
  };

  const pollJobStatus = async (jobId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 60;

      const runPoll = async () => {
        attempts++;
        
        if (attempts < 10) {
          setExtractionStep(0);
        } else if (attempts < 25) {
          setExtractionStep(1);
        } else if (attempts < 45) {
          setExtractionStep(2);
        }
        
        const progress = Math.min(95, Math.floor((attempts / maxAttempts) * 100));
        setExtractionProgress(progress);

        try {
          const status = await apiService.getJobStatus(jobId);

          if (status.status === "completed") {
            setExtractionProgress(100);
            setExtractionStep(2);

            const transactionsData = await apiService.getTransactions(jobId);
            let transactions: any[] = [];
            if (transactionsData.transactions) {
              transactions = transactionsData.transactions;
            } else if (transactionsData.value) {
              transactions = transactionsData.value;
            } else if (Array.isArray(transactionsData)) {
              transactions = transactionsData;
            }

            sessionStorage.setItem("currentJobId", jobId);
            sessionStorage.setItem("transactions", JSON.stringify(transactions));
            sessionStorage.setItem("transactionCount", String(transactions.length));

            setTransactionCount(transactions.length);
            setUploadState("complete");

            onExtractionComplete?.(jobId, transactions);

            setTimeout(() => {
              window.location.href = `/transactions/${jobId}`;
            }, 500);

            resolve();
            return;
          }

          if (status.status === "failed") {
            setUploadState("error");
            setUploadError(status.error || "Processing failed");
            reject(new Error(status.error || "Processing failed"));
            return;
          }

          if (attempts >= maxAttempts) {
            setUploadState("error");
            setUploadError("Processing timeout");
            reject(new Error("Processing timeout"));
            return;
          }

          setTimeout(runPoll, 3000);
        } catch (err: any) {
          setUploadState("error");
          setUploadError(err.message || "Polling failed");
          reject(err);
        }
      };

      runPoll();
    });
  };

  const handleFileUpload = async (file: File) => {
    setUploadState("uploading");
    setUploadError(null);
    setUploadProgress(0);
    setExtractionProgress(0);
    setExtractionStep(0);
    setTransactionCount(0);

    try {
      const quotaCheck = await checkQuotaBeforeUpload();
      if (!quotaCheck.allowed) {
        throw new Error(quotaCheck.message);
      }

      const tenantId = localStorage.getItem("tenantId") || "default-tenant";
      const userId = localStorage.getItem("userId") || "default-user";
      apiService.setTenantContext(tenantId, userId);

      const fileSizeMB = file.size / (1024 * 1024);
      const uploadTime = Math.min(3000, Math.max(1000, fileSizeMB * 500));

      const startTime = Date.now();
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(95, Math.round((elapsed / uploadTime) * 100));
        setUploadProgress(progress);
        if (progress >= 95) clearInterval(progressInterval);
      }, 100);

      const uploadResponse = await apiService.uploadStatement(file);
      clearInterval(progressInterval);

      if (!uploadResponse.success || !uploadResponse.jobId) {
        throw new Error(uploadResponse.error || "Upload failed");
      }

      const jobId = uploadResponse.jobId;
      setCurrentJobId(jobId);
      setUploadProgress(100);
      setUploadState("extracting");

      onUploadSuccess?.(jobId);

      const extractResponse = await apiService.extractStatement(jobId, file);
      if (!extractResponse.success) {
        throw new Error(extractResponse.error || "Extraction failed");
      }

      await pollJobStatus(jobId);
    } catch (error: any) {
      setUploadState("error");

      if (error?.response?.status === 402) {
        const used = error.response.data?.used;
        const limit = error.response.data?.limit;
        const requested = error.response.data?.requested;
        const msg = used !== undefined && limit !== undefined && requested !== undefined
          ? `Page limit exceeded. Used ${used}/${limit} pages. This statement requires ${requested} pages. Please buy extra pages or upgrade your plan.`
          : "Page limit exceeded. Please buy extra pages or upgrade your plan.";
        setUploadError(msg);
        onUploadError?.(msg);
        return;
      }

      if (error?.response?.status === 403) {
        const msg = error.response.data?.error || "No active subscription found. Please subscribe to continue.";
        setUploadError(msg);
        onUploadError?.(msg);
        return;
      }

      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || "Upload failed";
      setUploadError(errorMessage);
      onUploadError?.(errorMessage);
    }
  };

  const handleRetry = async () => {
    setUploadState("idle");
    setUploadError(null);
    setUploadProgress(0);
    setExtractionProgress(0);
    setExtractionStep(0);
    setCurrentJobId(null);
    setTransactionCount(0);
    sessionStorage.removeItem("currentJobId");
    sessionStorage.removeItem("transactions");
    sessionStorage.removeItem("transactionCount");
    await loadSubscription();
  };

  const handleManualNavigate = () => {
    if (currentJobId) {
      window.location.href = `/transactions/${currentJobId}`;
    }
  };

  const renderSubscriptionBanner = () => {
    if (subscriptionLoading) {
      return (
        <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: `1px solid ${mode === 'light' ? '#e6e9ee' : alpha(theme.palette.primary.main, 0.2)}`, backgroundColor: mode === 'light' ? '#fafbfd' : alpha(theme.palette.primary.main, 0.05), display: "flex", alignItems: "center", gap: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>Loading subscription usage...</Typography>
        </Box>
      );
    }

    if (!subscription) {
      return (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' } }}>
          {quotaMessage || "Unable to load subscription details."}
        </Alert>
      );
    }

    return (
      <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, border: `1px solid ${mode === 'light' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.2)}`, background: mode === 'light' ? "linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)" : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)` }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", md: "center" }, gap: 2, flexWrap: "wrap", mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Current Plan & Usage</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
              <Typography variant="h6" fontWeight={800} sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>{getPlanName(subscription.plan)}</Typography>
              <Chip label={getStatusLabel(subscription.status)} color={getStatusColor(subscription.status)} size="small" />
            </Box>
          </Box>
          <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>{usage.used.toLocaleString()} / {usage.totalAvailable.toLocaleString()} pages</Typography>
            <Typography variant="caption" color="text.secondary">{usage.remaining.toLocaleString()} remaining</Typography>
          </Box>
        </Box>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <InfoStat label="Base quota" value={usage.baseQuota} helper="Plan included" icon={<TrendingUpIcon sx={{ fontSize: 16 }} />} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <InfoStat label="Extra pages" value={usage.extraPages} helper={usage.extraPages > 0 ? "Added this period" : "None added"} icon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <InfoStat label="Total available" value={usage.totalAvailable} helper="Base + extra" />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <InfoStat label="Remaining" value={usage.remaining} helper="Still available" />
          </Grid>
        </Grid>

        <Box sx={{ position: 'relative', mb: 2 }}>
          <LinearProgress variant="determinate" value={usage.percent} sx={{ height: 12, borderRadius: 999, backgroundColor: mode === 'light' ? alpha(usageColor, 0.1) : alpha(usageColor, 0.2), "& .MuiLinearProgress-bar": { backgroundColor: usageColor, borderRadius: 999, backgroundImage: `linear-gradient(90deg, ${alpha(usageColor, 0.8)} 0%, ${usageColor} 100%)` } }} />
          <Box sx={{ position: 'absolute', top: -8, left: `${usage.percent}%`, transform: 'translateX(-50%)', width: 24, height: 24, borderRadius: '50%', bgcolor: usageColor, border: `3px solid ${mode === 'light' ? '#ffffff' : '#1E1E1E'}`, boxShadow: `0 2px 8px ${alpha(usageColor, 0.3)}` }} />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">{Math.round(usage.percent)}% of total available quota used</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {usage.percent >= 90 ? <><WarningAmberIcon sx={{ fontSize: 16, color: usageColor }} /><Typography variant="caption" sx={{ color: usageColor, fontWeight: 700 }}>Limit nearly reached</Typography></>
              : usage.percent >= 70 ? <Typography variant="caption" sx={{ color: usageColor, fontWeight: 700 }}>Approaching limit</Typography>
              : <Typography variant="caption" sx={{ color: usageColor, fontWeight: 700 }}>Healthy usage</Typography>}
          </Box>
        </Box>

        {/* Show quota messages for active subscriptions */}
        {hasActiveSubscription && !canUpload && quotaMessage && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{quotaMessage}</Alert>
        )}
        
        {hasActiveSubscription && canUpload && usage.percent >= 90 && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2, '& .MuiAlert-message': { color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' } }}>You're close to your limit. Buy extra pages or upgrade before your next upload.</Alert>
        )}

        {/* Extra pages buttons - only show for active subscriptions */}
        {/* Extra pages buttons - ONLY show for active subscriptions */}
{hasActiveSubscription && (
  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
    <Button
      variant="outlined"
      startIcon={<AddCircleOutlineIcon />}
      onClick={() => handleBuyExtraPages(100)}
      sx={{
        borderColor: theme.palette.secondary.main,
        color: theme.palette.secondary.main,
        '&:hover': {
          borderColor: theme.palette.secondary.dark,
          bgcolor: alpha(theme.palette.secondary.main, 0.05)
        }
      }}
    >
      Buy 100 Pages (${EXTRA_PAGES_OPTIONS[0].price})
    </Button>
    <Button
      variant="outlined"
      startIcon={<AddCircleOutlineIcon />}
      onClick={() => handleBuyExtraPages(500)}
      sx={{
        borderColor: theme.palette.secondary.main,
        color: theme.palette.secondary.main,
        '&:hover': {
          borderColor: theme.palette.secondary.dark,
          bgcolor: alpha(theme.palette.secondary.main, 0.05)
        }
      }}
    >
      Buy 500 Pages (${EXTRA_PAGES_OPTIONS[1].price})
    </Button>
    <Button
      variant="outlined"
      startIcon={<AddCircleOutlineIcon />}
      onClick={() => handleBuyExtraPages(1000)}
      sx={{
        borderColor: theme.palette.secondary.main,
        color: theme.palette.secondary.main,
        '&:hover': {
          borderColor: theme.palette.secondary.dark,
          bgcolor: alpha(theme.palette.secondary.main, 0.05)
        }
      }}
    >
      Buy 1000 Pages (${EXTRA_PAGES_OPTIONS[2].price})
    </Button>
    <Button
      variant="contained"
      onClick={() => navigate("/subscription")}
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        '&:hover': {
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
        }
      }}
    >
      Manage Subscription
    </Button>
  </Stack>
)}

{/* Show this message when NO active subscription */}
{!hasActiveSubscription && (
  <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
    <Typography variant="body2" fontWeight={600}>No Active Subscription</Typography>
    <Typography variant="caption">
      You need an active subscription before you can purchase extra pages. 
      Select a plan from the Subscription page to get started.
    </Typography>
    <Button 
      variant="outlined" 
      size="small" 
      onClick={() => navigate("/subscription")}
      sx={{ mt: 1 }}
    >
      View Plans
    </Button>
  </Alert>
)}
      </Paper>
    );
  };

  const renderContent = () => {
  switch (uploadState) {
    case "idle":
      return (
        <Box>
          {/* Premium PDF Format Card - Professional Design */}
          <Box
            sx={{
              mb: 4,
              borderRadius: 3,
              overflow: 'hidden',
              background: mode === 'light' 
                ? 'linear-gradient(135deg, #F8FAFF 0%, #F0F4FE 100%)'
                : 'linear-gradient(135deg, rgba(33, 79, 128, 0.15) 0%, rgba(198, 96, 47, 0.1) 100%)',
              border: `1px solid ${mode === 'light' 
                ? 'rgba(33, 79, 128, 0.15)' 
                : 'rgba(198, 96, 47, 0.25)'}`,
              position: 'relative',
            }}
          >
            {/* Decorative accent bar */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: 4,
                background: 'linear-gradient(90deg, #214F80 0%, #C6602F 100%)',
              }}
            />
            
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #214F80 0%, #C6602F 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: 18 }}>📄</Typography>
                </Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: mode === 'light' ? '#214F80' : '#C6602F' }}>
                  PDF Quality Guide
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Good Card */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 180,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: mode === 'light' ? 'rgba(56, 161, 105, 0.08)' : 'rgba(56, 161, 105, 0.12)',
                    border: `1px solid ${mode === 'light' ? 'rgba(56, 161, 105, 0.2)' : 'rgba(56, 161, 105, 0.3)'}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CheckCircleIcon sx={{ color: '#38A169', fontSize: 18 }} />
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#38A169' }}>
                      Recommended
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', color: mode === 'light' ? '#1A2A3A' : '#E5E5E5' }}>
                    Native Digital PDFs
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                    Downloaded directly from online banking
                  </Typography>
                </Box>

                {/* Bad Card */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 180,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: mode === 'light' ? 'rgba(221, 107, 32, 0.06)' : 'rgba(221, 107, 32, 0.1)',
                    border: `1px solid ${mode === 'light' ? 'rgba(221, 107, 32, 0.2)' : 'rgba(221, 107, 32, 0.3)'}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <WarningAmberIcon sx={{ color: '#DD6B20', fontSize: 18 }} />
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#DD6B20' }}>
                      Not Recommended
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', color: mode === 'light' ? '#1A2A3A' : '#E5E5E5' }}>
                    Scanned Paper PDFs
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                    Printed statements scanned or photos converted to PDF
                  </Typography>
                </Box>
              </Box>

              {/* Tip Banner */}
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: mode === 'light' ? 'rgba(33, 79, 128, 0.05)' : 'rgba(33, 79, 128, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ fontSize: 20 }}>💡</Typography>
                <Typography variant="caption" sx={{ color: mode === 'light' ? '#214F80' : '#C6602F', fontWeight: 500 }}>
                  Pro Tip: Look for "Download Statement" or "Export as PDF" in your online banking
                </Typography>
              </Box>
            </Box>
          </Box>

          <CloudUploadIcon sx={{ fontSize: 54, color: theme.palette.secondary.main, mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>Upload Bank Statement</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 580, mx: "auto" }}>Upload PDF statements from any financial institution worldwide. Our AI engine will automatically extract structured transactions.</Typography>
          <Typography variant="body2" color="text.secondary">Supported: PDF files only • Max size: 10MB</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>Your files are processed securely and are not shared with third parties for marketing or resale.</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
            <Button variant="contained" startIcon={<CloudUploadIcon />} disabled={!canUpload} sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white', '&:hover': { background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)` } }}>CHOOSE PDF FILE</Button>
          </Stack>
          {!canUpload && quotaMessage && <Typography variant="body2" color="error" sx={{ mt: 2 }}>{quotaMessage}</Typography>}
        </Box>
      );
    case "uploading":
      return (
        <Box sx={{ width: "100%" }}>
          <Stepper activeStep={0} sx={{ mb: 4, '& .MuiStepLabel-label': { color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' } }}>{steps.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}</Stepper>
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography variant="h6" gutterBottom sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>Uploading PDF...</Typography>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ width: "100%", mt: 2, mb: 1, height: 10, borderRadius: 999, backgroundColor: mode === 'light' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.2), '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.secondary.main, borderRadius: 999 } }} />
            <Typography variant="body2" color="text.secondary">{uploadProgress < 30 ? "Preparing file..." : uploadProgress < 60 ? "Uploading..." : uploadProgress < 90 ? "Almost done..." : "Processing..."}</Typography>
          </Box>
        </Box>
      );
    case "extracting":
      return (
        <Box sx={{ width: "100%" }}>
          <Stepper activeStep={1} sx={{ mb: 4, '& .MuiStepLabel-label': { color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' } }}>{steps.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}</Stepper>
          <Box sx={{ minHeight: "10vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
            <Lottie animationData={aiAnimation} loop style={{ height: 200 }} />
            <Typography variant="h6" sx={{ mt: 3, fontWeight: 600, color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>AI is analyzing and structuring financial data...</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{extractionSteps[extractionStep]}</Typography>
          </Box>
          <Box sx={{ mt: 2, width: "100%" }}>
            <Typography variant="body1" fontWeight={600} sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>Processing Financial Data</Typography>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, opacity: extractionStep >= 0 ? 1 : 0.4 }}>
                {extractionStep >= 0 ? <CheckCircleIcon sx={{ mr: 1, color: theme.palette.success.main, fontSize: 16 }} /> : <CircularProgress size={16} sx={{ mr: 1 }} />}
                <Typography variant="body2">Reading PDF structure</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, opacity: extractionStep >= 1 ? 1 : 0.4 }}>
                {extractionStep >= 1 ? <CheckCircleIcon sx={{ mr: 1, color: theme.palette.success.main, fontSize: 16 }} /> : extractionStep === 0 ? <CircularProgress size={16} sx={{ mr: 1 }} /> : <CircularProgress size={16} sx={{ mr: 1, opacity: 0.3 }} />}
                <Typography variant="body2">Detecting transactions</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, opacity: extractionStep >= 2 ? 1 : 0.4 }}>
                {extractionStep >= 2 ? <CheckCircleIcon sx={{ mr: 1, color: theme.palette.success.main, fontSize: 16 }} /> : extractionStep === 1 ? <CircularProgress size={16} sx={{ mr: 1 }} /> : <CircularProgress size={16} sx={{ mr: 1, opacity: 0.3 }} />}
                <Typography variant="body2">Applying AI categorisation</Typography>
              </Box>
            </Box>
          </Box>
          <LinearProgress variant="determinate" value={extractionProgress} sx={{ height: 8, borderRadius: 20, backgroundColor: mode === 'light' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.2), mt: 2, '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.secondary.main, borderRadius: 20 } }} />
          <Typography variant="caption" sx={{ mt: 1, display: "block", textAlign: "right" }}>{extractionProgress}% complete</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>This usually takes 20-60 seconds depending on statement size</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>Don't close this window. You'll be automatically redirected when complete.</Typography>
        </Box>
      );
    case "complete":
      return (
        <Box>
          <CheckCircleIcon sx={{ fontSize: 60, color: theme.palette.success.main, mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>Extraction Complete!</Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>Found <strong>{transactionCount}</strong> transactions!</Typography>
          <Button variant="contained" color="primary" size="large" onClick={handleManualNavigate} sx={{ mt: 2, mb: 2, background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`, color: '#0A1E3C', '&:hover': { background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)` } }}>View {transactionCount} Transactions</Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Redirecting automatically in a few seconds...</Typography>
        </Box>
      );
    case "error":
      return (
        <Box>
          <Alert severity="error" sx={{ borderRadius: 2 }} action={<Button color="inherit" size="small" onClick={handleRetry} sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}>RETRY</Button>}>{uploadError}</Alert>
          {uploadError?.toLowerCase().includes("page limit") && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => handleBuyExtraPages(100)} sx={{ borderColor: theme.palette.secondary.main, color: theme.palette.secondary.main, '&:hover': { borderColor: theme.palette.secondary.dark, bgcolor: alpha(theme.palette.secondary.main, 0.05) } }}>Buy 100 Pages (${EXTRA_PAGES_OPTIONS[0].price})</Button>
              <Button variant="outlined" onClick={() => handleBuyExtraPages(500)} sx={{ borderColor: theme.palette.secondary.main, color: theme.palette.secondary.main, '&:hover': { borderColor: theme.palette.secondary.dark, bgcolor: alpha(theme.palette.secondary.main, 0.05) } }}>Buy 500 Pages (${EXTRA_PAGES_OPTIONS[1].price})</Button>
              <Button variant="contained" onClick={() => navigate("/subscription")} sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white', '&:hover': { background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)` } }}>Manage Subscription</Button>
            </Stack>
          )}
        </Box>
      );
    default: return null;
  }
};

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
  <span style={{ color: '#214F80' }}>Extract</span>
  <span style={{ color: '#C6602F' }}>Fast</span>
  <span style={{ color: '#0A2540' }}> Statement Processing</span>
</Typography>
        <Typography variant="body1" color="text.secondary">Upload PDF statements and let AI extract transactions automatically</Typography>
      </Box>
      
      {renderSubscriptionBanner()}
      
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: `1px solid ${mode === 'light' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.2)}`, background: mode === 'light' ? "linear-gradient(180deg, #ffffff 0%, #fcfdff 100%)" : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)` }}>
        <Box {...(uploadState === "idle" && canUpload ? getRootProps() : {})} sx={{ border: "2px dashed", borderColor: !canUpload ? theme.palette.error.light : isDragActive ? theme.palette.secondary.main : mode === 'light' ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.primary.main, 0.5), borderRadius: 2, p: 4, textAlign: "center", cursor: uploadState === "idle" && canUpload ? "pointer" : "default", backgroundColor: !canUpload ? alpha(theme.palette.error.main, 0.05) : isDragActive ? alpha(theme.palette.secondary.main, 0.05) : mode === 'light' ? alpha(theme.palette.primary.main, 0.02) : alpha(theme.palette.primary.main, 0.1), transition: "all 0.3s", minHeight: 360, display: "flex", alignItems: "center", justifyContent: "center", opacity: uploadState === "idle" && !canUpload ? 0.7 : 1, '&:hover': { borderColor: uploadState === "idle" && canUpload ? theme.palette.secondary.main : undefined, backgroundColor: uploadState === "idle" && canUpload ? alpha(theme.palette.secondary.main, 0.08) : undefined } }}>
          <input {...getInputProps()} disabled={uploadState !== "idle" || !canUpload} />
          {renderContent()}
        </Box>
        
        {uploadState === "idle" && (
          <Grid container spacing={3} sx={{ mt: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}><Box sx={{ textAlign: 'center' }}><AutoAwesomeIcon sx={{ color: theme.palette.secondary.main, mb: 1 }} /><Typography variant="body2" fontWeight={700} sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>AI Extraction</Typography><Typography variant="caption" color="text.secondary" display="block">Smart transaction detection</Typography></Box></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}><Box sx={{ textAlign: 'center' }}><CloudUploadIcon sx={{ color: theme.palette.secondary.main, mb: 1 }} /><Typography variant="body2" fontWeight={700} sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>Global Banks</Typography><Typography variant="caption" color="text.secondary" display="block">Works with any PDF statement</Typography></Box></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}><Box sx={{ textAlign: 'center' }}><DownloadIcon sx={{ color: theme.palette.secondary.main, mb: 1 }} /><Typography variant="body2" fontWeight={700} sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>Instant CSV Export</Typography><Typography variant="caption" color="text.secondary" display="block">Ready for Excel & accounting</Typography></Box></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}><Box sx={{ textAlign: 'center' }}><SecurityIcon sx={{ color: theme.palette.secondary.main, mb: 1 }} /><Typography variant="body2" fontWeight={700} sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>Secure Processing</Typography><Typography variant="caption" color="text.secondary" display="block">No data retained unless you choose</Typography></Box></Grid>
          </Grid>
        )}
      </Paper>

      {/* Popup for extra pages restriction */}
      <Snackbar
        open={popupOpen}
        autoHideDuration={6000}
        onClose={() => setPopupOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setPopupOpen(false)} severity="info" sx={{ width: '100%', borderRadius: 2 }}>
          {popupMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UploadZone;